import React, { useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import { KonnectAnalytics } from '../../../KonnectAnalytics/konnectAnalytics';

import { styles } from '../WorkingHours.styles';
import { TimeSlot, WorkingDay, TimeSlotEditorState } from '../WorkingHours.types';
import TimeSlotBar from './TimeSlotBar';
import HoursAxis from './HoursAxis';
import { WorkingHoursEditorValidation } from '../workingHoursSelectors';
import { MINUTES_PER_DAY, SLOT_STEP_MINUTES, DIRECT_CREATE_DEFAULT_DURATION_MINUTES } from '../workingHoursConstants';
import { getCarryInMinutesForDay } from '../utils/slotOverlapUtils';
import { minutesToTime12h } from '../utils/slotLabelUtils';

type DayRowProps = {
  day: WorkingDay;
  days: WorkingDay[];
  // Last saved (server) state of this day — slots not matching it are rendered as drafts.
  savedDay?: WorkingDay;
  selectedSite: string;
  editor: TimeSlotEditorState;
  editorValidation: WorkingHoursEditorValidation | null;
  onToggleDay: (dayId: string) => void;
  onAddSlot: (payload: { dayId: string; slotId: string }) => void;
  onAddSlotAt: (payload: {
    dayId: string;
    slotId: string;
    startMinutes: number;
    durationMinutes: number;
  }) => void;
  onUpdateSlot: (payload: {
    dayId: string;
    slotId: string;
    startMinutes: number;
    endMinutes: number;
  }) => void;
  onRemoveSlot: (payload: { dayId: string; slotId: string }) => void;
  onRestoreDay: (payload: { dayId: string; slots: TimeSlot[]; enabled: boolean }) => void;
  onOpenEditor: (payload: { dayId: string; slotId: string }) => void;
  onCloseEditor: () => void;
  onUpdateEditorDraft: (payload: {
    startMinutes?: number;
    endMinutes?: number;
    overnight?: boolean;
  }) => void;
  onSetEditorApplyDays: (payload: { applyDays: string[] }) => void;
  onApplyEditor: () => void;
  // Persists the whole configuration to the backend (same call the page-level "Save
  // changes" button makes) — clicking Save on a row means the user wants that sent too.
  onSaveChanges: (trigger?: 'save_button') => void;
  readOnly: boolean;
};

const DayRow = React.memo(({
  day,
  days,
  savedDay,
  selectedSite,
  editor,
  editorValidation,
  onToggleDay,
  onAddSlot,
  onAddSlotAt,
  onUpdateSlot,
  onRemoveSlot,
  onRestoreDay,
  onOpenEditor,
  onCloseEditor,
  onUpdateEditorDraft,
  onSetEditorApplyDays,
  onApplyEditor,
  onSaveChanges,
  readOnly,
}: DayRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot] = useState<{ slots: TimeSlot[]; enabled: boolean } | null>(null);

  const sortedSlots = useMemo(
    () => [...day.slots].sort((a, b) => a.startMinutes - b.startMinutes),
    [day.slots]
  );

  const isSlotDraft = useCallback(
    (slot: TimeSlot) =>
      !savedDay?.enabled ||
      !savedDay.slots.some(
        saved => saved.startMinutes === slot.startMinutes && saved.endMinutes === slot.endMinutes
      ),
    [savedDay]
  );

  const { prevDay, carryInMinutes } = useMemo(() => {
    const dayIndex = days.findIndex(d => d.id === day.id);
    return {
      prevDay: dayIndex >= 0 ? days[(dayIndex + days.length - 1) % days.length] : undefined,
      carryInMinutes: getCarryInMinutesForDay(days, day.id),
    };
  }, [days, day.id]);

  const cancelEdit = useCallback(
    (snap: { slots: TimeSlot[]; enabled: boolean } | null) => {
      if (snap) {
        onRestoreDay({ dayId: day.id, slots: snap.slots, enabled: snap.enabled });
      }
      setIsEditing(false);
      setSnapshot(null);
    },
    [day.id, onRestoreDay]
  );

  const dayTitle = day.enabled ? day.name : `${day.name} (Disabled)`;

  const enterEditMode = () => {
    if (readOnly || isEditing) return;
    setSnapshot({ slots: day.slots, enabled: day.enabled });
    setIsEditing(true);
  };

  const handleRemoveSlot = useCallback(
    (payload: { dayId: string; slotId: string }, source: 'bar' | 'editor' = 'bar') => {
      enterEditMode();
      onRemoveSlot(payload);
      KonnectAnalytics.workingHoursRemoveSlot({ ...payload, site: selectedSite, source });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRemoveSlot, selectedSite]
  );

  // Popover Cancel only discards *this* slot when it didn't exist before the current edit
  // session started (a fresh default slot from "+ Add time frame" / enabling the day /
  // double-click that was never confirmed via Apply) — an already-saved slot reopened for
  // editing is untouched by the draft until Apply, so it's simply left as-is.
  const handleEditorCancel = useCallback(() => {
    const slotId = editor?.slotId;
    if (slotId && snapshot && !snapshot.slots.some(s => s.id === slotId)) {
      onRemoveSlot({ dayId: day.id, slotId });
    }
    if (slotId) {
      KonnectAnalytics.productionHoursCancelSlotEdit({ dayId: day.id, slotId, site: selectedSite });
    }
    onCloseEditor();
  }, [editor, snapshot, day.id, onRemoveSlot, onCloseEditor, selectedSite]);

  const handleDeleteFromEditor = useCallback(
    (slotId: string) => {
      handleRemoveSlot({ dayId: day.id, slotId }, 'editor');
      onCloseEditor();
    },
    [handleRemoveSlot, day.id, onCloseEditor]
  );

  const handleSetEditorApplyDays = useCallback(
    (payload: { applyDays: string[] }) => {
      onSetEditorApplyDays(payload);
      if (editor) {
        KonnectAnalytics.productionHoursChangeApplyDays({
          dayId: editor.dayId,
          slotId: editor.slotId,
          applyDays: payload.applyDays,
          site: selectedSite,
        });
      }
    },
    [onSetEditorApplyDays, editor, selectedSite]
  );

  const handleApplyEditor = useCallback(() => {
    if (editor) {
      KonnectAnalytics.productionHoursApplySlot({
        dayId: editor.dayId,
        slotId: editor.slotId,
        startMinutes: editor.startMinutes,
        endMinutes: editor.endMinutes,
        overnight: editor.overnight,
        applyDaysCount: editor.applyDays.length,
        site: selectedSite,
      });
    }
    // Apply only commits the edit as a page-level draft (sent on Save / Save changes);
    // ending the row session keeps a later click-away from reverting it.
    setIsEditing(false);
    setSnapshot(null);
    onApplyEditor();
  }, [editor, onApplyEditor, selectedSite]);

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setSnapshot(null);
    KonnectAnalytics.productionHoursDayRowAction({ action: 'save', dayId: day.id, site: selectedSite });
    onSaveChanges('save_button');
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    KonnectAnalytics.productionHoursDayRowAction({ action: 'cancel', dayId: day.id, site: selectedSite });
    cancelEdit(snapshot);
  };

  // The time-editor popover (and its Select dropdowns) render via a portal outside this
  // row's DOM subtree, so a plain outside-click check would treat clicks inside it as
  // "away" and wipe unsaved edits. Ignore anything inside a MUI popover/modal portal.
  // Also ignore clicks that land inside ANY other day row (`[data-day-row]`) — days are
  // editable independently and concurrently (e.g. disabling Monday, then Tuesday, should
  // leave Monday's pending change and its own Save/Cancel in place, not revert it).
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest?.('.MuiPopover-root, .MuiModal-root, .MuiPopper-root')) return;
    if (target.closest?.('[data-day-row]')) return;
    if (isEditing) cancelEdit(snapshot);
  };

  // Double-click an empty spot on the timeline to create a frame there directly,
  // then open the editor so the user can adjust it (or delete it if it was a mis-click).
  const handleTrackDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly || !day.enabled) return;
    if ((e.target as HTMLElement).closest('.slot-bar')) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const rawMinutes = ((e.clientX - rect.left) / rect.width) * MINUTES_PER_DAY;
    const startMinutes = Math.max(
      0,
      Math.min(MINUTES_PER_DAY, Math.round(rawMinutes / SLOT_STEP_MINUTES) * SLOT_STEP_MINUTES)
    );
    const slotId = `${day.id}-slot-${Date.now()}`;
    onAddSlotAt({ dayId: day.id, slotId, startMinutes, durationMinutes: DIRECT_CREATE_DEFAULT_DURATION_MINUTES });
    KonnectAnalytics.workingHoursAddSlot({ dayId: day.id, slotId, site: selectedSite });
    onOpenEditor({ dayId: day.id, slotId });
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        data-day-row={day.id}
        sx={isEditing ? styles.dayRowEditing : styles.dayRow}
        onClick={enterEditMode}>
        <Box sx={styles.dayHeaderRow}>
          <Box sx={styles.dayLeftSection}>
            <Switch
              checked={day.enabled}
              onChange={() => {
                if (!day.enabled) {
                  enterEditMode();
                  onToggleDay(day.id);
                  // Re-enabling a day that already has slots (previously configured, just
                  // switched off) should bring them back as-is — only a genuinely empty day
                  // needs a starter slot, and even then the user shapes it in the editor.
                  if (day.slots.length === 0) {
                    const slotId = `${day.id}-slot-${Date.now()}`;
                    onAddSlot({ dayId: day.id, slotId });
                    KonnectAnalytics.workingHoursAddSlot({
                      dayId: day.id,
                      slotId,
                      site: selectedSite,
                    });
                    onOpenEditor({ dayId: day.id, slotId });
                  }
                } else {
                  // If we're still mid-edit on a session that started by enabling this day
                  // (the starter slot from above was never confirmed via Save/Apply), turning
                  // it back off should discard that slot entirely, not just flip `enabled`.
                  if (isEditing && snapshot?.enabled === false) {
                    cancelEdit(snapshot);
                    onCloseEditor();
                  } else {
                    enterEditMode();
                    onToggleDay(day.id);
                  }
                  KonnectAnalytics.workingHoursToggleDay({
                    dayId: day.id,
                    enabled: false,
                    site: selectedSite,
                  });
                }
              }}
              size="small"
              disabled={readOnly}
            />
            <Typography sx={day.enabled ? styles.dayName : styles.dayNameDisabled}>
              {dayTitle}
            </Typography>
          </Box>

          <Box sx={styles.dayRowActions}>
            {isEditing && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  sx={styles.saveButton}>
                  Save
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancel}
                  sx={styles.cancelButton}>
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Box
          data-timeline-track
          onDoubleClick={handleTrackDoubleClick}
          sx={day.enabled ? styles.timelineTrack : styles.timelineTrackDisabled}>
          {!day.enabled && (
            <Box sx={styles.timelineEmptyHint}>Not a working day — enable to add hours</Box>
          )}
          {carryInMinutes > 0 && (
            <>
              <Box
                sx={{ ...styles.carryIn, width: `${(carryInMinutes / MINUTES_PER_DAY) * 100}%` }}
                title={`Continuation of ${prevDay?.name} night shift — edit it on ${prevDay?.name}`}>
                <Box component="span" sx={styles.carryInLock}>
                  🔒
                </Box>
                from {prevDay?.name} · until {minutesToTime12h(carryInMinutes)}
              </Box>
              <Box
                sx={{
                  ...styles.reserveEdge,
                  left: `${(carryInMinutes / MINUTES_PER_DAY) * 100}%`,
                }}
              />
            </>
          )}
          {day.enabled &&
            sortedSlots.map((slot, index) => (
              <TimeSlotBar
                key={slot.id}
                slot={slot}
                index={index}
                dayId={day.id}
                selectedSite={selectedSite}
                allSlots={sortedSlots}
                isDraft={isSlotDraft(slot)}
                days={days}
                editor={editor}
                editorValidation={editorValidation}
                onUpdateSlot={onUpdateSlot}
                onRemoveSlot={handleRemoveSlot}
                onDeleteFromEditor={handleDeleteFromEditor}
                onOpenEditor={onOpenEditor}
                onCloseEditor={handleEditorCancel}
                onUpdateEditorDraft={onUpdateEditorDraft}
                onSetEditorApplyDays={handleSetEditorApplyDays}
                onApplyEditor={handleApplyEditor}
                onEditStart={enterEditMode}
                readOnly={readOnly}
              />
            ))}
        </Box>

        <HoursAxis />
      </Box>
    </ClickAwayListener>
  );
});

DayRow.displayName = 'DayRow';

export default DayRow;
